/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react"
import {toast} from "react-hot-toast";
import {usePostRequest} from "../../../../api/helper";
import ArgosButton from "../../Buttons/ArgosButton";
import ArgosFlexbox from "../../Containers/ArgosFlexbox";
import ArgosMenuItem from "../../Form/Select/ArgosMenuItem";
import Loader from "../../Loader/Loader";
import InputLabel from '@mui/material/InputLabel';
import ListItemText from "@mui/material/ListItemText";
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import {getOptionsListForType, OBJECT_TYPE_STR, PACKAGE_TYPE_STR, ROAD_TYPE_TYPE_STR, USE_CASE_TYPE_STR} from "../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/constants";
import {cloneDeep, isEmpty} from "lodash";
import {IFetchObjectsResponse, IProcessedObjectData, TPmoAndMiscProfileNames} from "../../../../constants/objects/ObjectTypes/common";
import {getProfileDisplayName, processObjectData, sortObjectData} from "../../../../helpers/objects/helpers";
import ArgosTextField from "../../Form/TextField/ArgosTextField";
import {ListItemIcon} from "@mui/material";
import ArgosCheckbox from "../../Form/Checkbox/ArgosCheckbox";
import {selectCoverageRequirementsDataState} from "../../../../redux/ducks/CoverageRequirements/slice";
import {useAppSelector} from "../../../../redux/hooks";
import ADAS_PROGRAMS_CONSTANTS from "../../../pages/AdasPrograms/constants";

interface Props {
  onClose: any;
  multipleSelect: boolean;
  propertyType: string;
  onSave?: any;
  data?: any;
  title?: string;
  configState?: any;
  setBulkConfig?: Function;
}
/**
 * Get ids of objects that match the given name from the already selected objects.
 * @param name 
 * @param alreadySelectedObjects 
 * @returns Array of object IDs that match the given name from the already selected objects.
 */
export const getObjectIdsFromName = (name: string, alreadySelectedObjects: any[] | undefined): string[] => {
  const matchingObjects = (alreadySelectedObjects ?? []).filter((obj: any) => obj.name === name);
  return matchingObjects.map((obj: any) => obj.objectId);
};
/**
 * Tally the count of selected variants for a given name based on object IDs.
 *
 * This function calculates the number of selected variants for each version of a given name
 * and returns the result either as a JSON object or as a JSX element string representation.
 *
 * @param name - The name of the object to tally versions for.
 * @param returnJson - A boolean flag indicating whether to return the result as JSON (default: false).
 * @param selectedCategory - The category of the object.
 * @param alreadySelectedObjects - An array of already selected objects, used to filter the variants.
 * @param objCatToData - The processed object data, structured by category and name.
 * @returns If `returnJson` is true, returns a `Record<string, string>` where the keys are version identifiers
 *          and the values are strings in the format "matchedCount/totalVariants".
 *          If `returnJson` is false, returns a JSX element representing the tally string.
 */
export const tallyVersionsCount = (
  selectedCategory: string,
  name: string,
  alreadySelectedObjects: any[] | undefined,
  objCatToData: IProcessedObjectData,
  returnJson: boolean = false
): Record<string, string> | React.ReactNode => {
  const objids = getObjectIdsFromName(name, alreadySelectedObjects);
  const tally: Record<string, string> = {};

  if (objids.length) {
    Object.entries(objCatToData[selectedCategory][name]).forEach(([rootKey, entry]) => {
      const variantIds = Object.values(entry.variants);
      const totalVariants = variantIds.length;
      const matchedCount = variantIds.filter((id) => objids.includes(id)).length;
      if (matchedCount > 0) {
        tally[rootKey] = `${matchedCount}/${totalVariants}`;
      }
    });
  }

  if (returnJson) {
    return tally;
  } else {
    const tallyString = Object.entries(tally)
      .map(([version, count]) => `v${version}: ${count}`)
      .join(", ");
    return tallyString ? (
      <span>
        {name} <span style={{ color: "green" }}>({tallyString} variants selected)</span>
      </span>
    ) : (
      <span>{name}</span>
    );
  }
};
const AddPropertyDialog: React.FC<Props> = (props) => {
  const [isLoadingObjectData, setIsLoadingObjectData] = useState(false)

  const selectedDatasetPropertyType: string = props.propertyType

  /**
   * Object property state
   */
    // State of dropdowns
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [selectedName, setSelectedName] = useState<string>("");
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [onlyVersion, setOnlyVersion] = useState<number | null>(null);
  type SelectedVariantType = Array<string | TPmoAndMiscProfileNames>;
  const [selectedVariant, setSelectedVariant] = useState<SelectedVariantType>([]);
  // Dropdown change handlers
  const handleCategoryChange = (event: any) => {
    setSelectedCategory(event.target.value);
    setSelectedName("");
    setSelectedVersion(null);
    setSelectedVariant([]);
  };
  const handleNameChange = (event: any) => {
    setSelectedName(event.target.value);
    const tally = tallyVersionsCount(selectedCategory, event.target.value, alreadySelectedObjects, objCatToData, true) as Record<string, string>; // Tally versions count for the selected name
    if (Object.keys(tally).length > 0) {
      const version = Number(Object.keys(tally)[0])
      // Set the version as selected
      setSelectedVersion(version);
      // Disable all other versions in the version dropdown except for this version
      setOnlyVersion(version); 
      const matchingVariants = version !== null
        ? Object.keys(objCatToData[selectedCategory]?.[event.target.value]?.[version]?.variants || {}).filter(
        (variant) => alreadySelectedObjects.some((object: any) => object.objectId === objCatToData[selectedCategory][event.target.value][version].variants[variant])
          )
        : [];
      setSelectedVariant(matchingVariants);

    }
    else {
      setOnlyVersion(null);
      setSelectedVariant([]);
      const versions = objCatToData[selectedCategory]?.[event.target.value] ? Object.keys(objCatToData[selectedCategory][event.target.value]) : [];
      const highestVersion = versions.length > 0 ? Math.max(...versions.map(Number)) : null;
      setSelectedVersion(highestVersion);
    }
  };
  const handleVersionChange = (event: any) => {
    setSelectedVersion(event.target.value);
  };
  const handleVariantChange = (event: any) => {
    const value = event.target.value;
    if (value[value.length - 1] === "all") { // If user hit "Select All" checkbox
      const allVariants = selectedVersion !== null 
        ? Object.keys(objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants || {}) 
        : [];
      setSelectedVariant(selectedVariant.length === allVariants.length ? [] : allVariants);
    } else {
      setSelectedVariant(value);
    }
  };
  // Fetch object data when the component mounts
  useEffect(() => {
    if (selectedDatasetPropertyType === OBJECT_TYPE_STR) {
      fetchObjectData()
    } else {
    }
  }, [])
  // Setup for fetching object data
  const fetchObjectsHandle = usePostRequest('/HttpTriggerCoverageRequirements?request_operation=get&request_type=getRwoData')


  const fetchObjectData = () => {
    setIsLoadingObjectData(true)
    fetchObjectsHandle().then((fetchObjectsResponse: IFetchObjectsResponse) => {
      const processedObjectData = processObjectData(fetchObjectsResponse)
      const sortedProcessedObjectData = sortObjectData(processedObjectData)
      setObjCatToData(sortedProcessedObjectData);
    }).catch((err) => {
      console.log(err)
      toast.error("Failed to load object data")
    }).finally(() => {
      setIsLoadingObjectData(false)
    })
  }
  // State to store the processed object data
  const [objCatToData, setObjCatToData] = useState<IProcessedObjectData>({})



  // Get the object categories to display in the dropdown
  const objCats = Object.keys(objCatToData)

  /**
   * Save the selected property data to redux (adding to driving scenario properties).
   */
  const saveData = () => {

    if (selectedDatasetPropertyType === OBJECT_TYPE_STR) {
      const newPropertiesToSave: any = []
      selectedVariant.forEach((option) => {
        let link: any = ""
        // Save new property object
        let newObj: any = {}
        // Get relevant object data ID given what is selected in dropdowns
        if (selectedCategory && selectedName && selectedVersion !== null && selectedVariant) {
          const objectData = objCatToData[selectedCategory][selectedName][selectedVersion];
          const variantId = objectData.variants[option];
          if (!variantId) {
            return null;
          }
          newObj['objectId'] = variantId // This is the ID of the object variant   
        }
        newObj['type'] = selectedDatasetPropertyType // property type eg Object, Road Type, Package
        newObj['category'] = selectedCategory // this is only for objects
        newObj['name'] = selectedName
        newObj['variantName'] = option
        newObj['version'] = selectedVersion

        newObj['property_link'] = link
        newPropertiesToSave.push(newObj)
      })

      if (props.onSave) {
        props.onSave(newPropertiesToSave, true, undefined)
      }
      if (props.setBulkConfig) {
        props.setBulkConfig(newPropertiesToSave)
      }

    } else {
      if (multipleSelect) {
        const newPropertiesToSave: any = []
        selectedOptions.forEach((option) => {
          let link: any = ""

          let newObj: any = {}
          newObj['type'] = selectedDatasetPropertyType
          newObj['category'] = selectedCategory
          newObj['property_link'] = link
          newObj['name'] = option
          newPropertiesToSave.push(newObj)
        })
        if (props.onSave) {
          props.onSave(newPropertiesToSave, true, undefined)
        }
        if (props.setBulkConfig) {
          props.setBulkConfig(newPropertiesToSave)
        }
      } else {
        // Identify the link for the option if it exists
        let link: any = ""

        // Save new property object
        let newObj: any = {}
        newObj['type'] = selectedDatasetPropertyType
        newObj['category'] = selectedCategory
        newObj['name'] = selectedOption
        newObj['property_link'] = link
        if (selectedDatasetPropertyType === PACKAGE_TYPE_STR) {
          /**
           * We display the name of the package with program name and model year separated by a space. But, the id
           * has a hyphen between the program name and model year, because this is the way Adas Programs App stores
           * the programs.
           */
          newObj['name'] = `${selectedAdasProgramName} ${selectedModelYear}`
          // meta
          const metaData: any = {}
          metaData['modelYear'] = selectedModelYear
          metaData['programName'] = selectedAdasProgramName
          // loop through adasProgramData and find a match of this program name and model year
          let adasProgramItemData: any = {}
          adasProgramsData.forEach((element: any) => {
            if (element?.data?.programName === selectedAdasProgramName && element?.data?.modelYear === selectedModelYear) {
              adasProgramItemData = element
            }
          })
          metaData['adasProgramId'] = adasProgramItemData.adasProgramId
          newObj['metaData'] = metaData
        }
        if (props.onSave) {
          props.onSave([cloneDeep(newObj)], true, undefined)
        }
        if (props.setBulkConfig) {
          props.setBulkConfig([cloneDeep(newObj)])
        }
      }
    }

    props.onClose()
  }

  // Use case
  const [selectedOption, setSelectedOption] = useState<string>("")
  const handleUseCaseChange = (event: any) => {
    setSelectedOption(event.target.value)
  }

  // Package
  const [selectedModelYear, setSelectedModelYear] = useState<string>(props.data?.rowData?.metaData?.modelYear ?? "")
  const [selectedAdasProgramName, setSelectedAdasProgramName] = useState<string>(props.data?.rowData?.metaData?.programName ?? "")
  const [adasProgramsToModelYears, setAdasProgramsToModelYears] = useState<any>({})

  useEffect(() => {
    if (selectedDatasetPropertyType === PACKAGE_TYPE_STR) {
      fetchPackageData()
    } else if(selectedDatasetPropertyType !== OBJECT_TYPE_STR) {
      setOptionsList(getOptionsListForType(selectedDatasetPropertyType))
    }
  }, [])
  const [adasProgramsData, setAdasProgramsData] = useState([])
  const packagesDataFetchHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchAdasPrograms)
  const [isLoading, setIsLoading] = useState(false)
  const fetchPackageData = () => {
    setIsLoading(true)
    packagesDataFetchHandle().then((data) => {
      const _adasProgramsToModelYears: any = {}
      data.forEach((element: any) => {
        const elementData = element?.data
        const programName = elementData?.programName
        const modelYear = elementData?.modelYear
        if (programName in _adasProgramsToModelYears) {
          _adasProgramsToModelYears[programName].push(modelYear)
        } else {
          _adasProgramsToModelYears[programName] = [modelYear]
        }
      })
      setAdasProgramsData(data)
      setAdasProgramsToModelYears(_adasProgramsToModelYears)
      setIsLoading(false)
    }).catch((e) => {
      console.error(e)
      toast.error("Failed to fetch Adas Programs data")
      setIsLoading(false)
    })
  }
  const coverageRequirementsData = useAppSelector(selectCoverageRequirementsDataState)
  const checkForAlreadySelectedObjects = () => {
    if (!isEmpty(props.configState)) {
      return props.configState[selectedDatasetPropertyType]?.filter((prop: any) => prop.type === (selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? ROAD_TYPE_TYPE_STR : OBJECT_TYPE_STR)) ?? []
    }
    // Objects already added to the table but not yet saved to the database
    else if (coverageRequirementsData && coverageRequirementsData.data) {
      return coverageRequirementsData.data?.driving_scenario_properties?.filter((prop: any) => prop.type === (selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? ROAD_TYPE_TYPE_STR : OBJECT_TYPE_STR)) ?? []
    } else {
      return []
    }
  }
  const alreadySelectedObjects = checkForAlreadySelectedObjects()

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]) // List of checked options


  const [optionsList, setOptionsList] = useState<string[]>([])
  const multipleSelect = props.multipleSelect
  const isAllInCurrentListAlreadySelected = selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? optionsList.every((option: any) => isOptionAlreadySelected(option, selectedDatasetPropertyType, props.configState, alreadySelectedObjects)) : false
  const isAllSelected = (selectedOptions.length > 0 && selectedOptions.length === (selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? optionsList.length : false)) || isAllInCurrentListAlreadySelected

  const handleOptionsChange = (event: any) => {
    const value = event.target.value
    if (multipleSelect) {
      if (value[value.length - 1] === "all") { // If user hit "Select All" checkbox
        setSelectedOptions(selectedOptions.length === optionsList.length ? [] : optionsList)
      } else {
        setSelectedOptions(value)
      }
    } else {
      setSelectedOption(value)
    }
  }

  const WIDTH = 500
  const isSavePackageDisabled = !adasProgramsToModelYears[selectedAdasProgramName]?.includes(selectedModelYear) && selectedDatasetPropertyType === PACKAGE_TYPE_STR
  const isNormalSaveDisabled = (multipleSelect ? selectedOptions.length === 0 : selectedOption === "") && selectedDatasetPropertyType !== PACKAGE_TYPE_STR
  /**
   * Check if the save button should be disabled based on the selected version and variants.
   * @returns Returns true if the save button should be disabled based on the selected version and variants.
   */
  const checkVersionVariants = () => {
    return selectedVariant.length === 0 || (
      selectedVersion !== null &&
      objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants &&
      Object.values(objCatToData[selectedCategory][selectedName][selectedVersion].variants).every((variantId: string) =>
        alreadySelectedObjects.some((object: any) => object.objectId === variantId)
      )
    )
  }
  const isSaveDisabled = selectedDatasetPropertyType === OBJECT_TYPE_STR 
    ? checkVersionVariants()
    : (isSavePackageDisabled || isNormalSaveDisabled);
  return (
    <Loader isLoading={isLoadingObjectData}>
      <ArgosFlexbox vertical style={{justifyContent: "center", alignItems: "center"}}>

        {selectedDatasetPropertyType === OBJECT_TYPE_STR && (
          <>
            {/*Category dropdown*/}
            <FormControl sx={{width: WIDTH}}>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                label="Category"
                onChange={handleCategoryChange}
                variant="standard"
              >
                {objCats.map((category: string) => (
                  <ArgosMenuItem key={category} value={category}>
                    <ListItemText primary={category}/>
                  </ArgosMenuItem>
                ))}
              </Select>
            </FormControl>

            {/*Name dropdown*/}
            {
              <FormControl sx={{width: WIDTH}}>
                <InputLabel id="name-select-label">Name</InputLabel>
                <Select
                  labelId="name-select-label"
                  id="name-select"
                  value={selectedName}
                  label="Name"
                  onChange={handleNameChange}
                  variant="standard"
                >
                  {objCatToData[selectedCategory] && Object.keys(objCatToData[selectedCategory]).map((name: string) => (
                      <ArgosMenuItem key={name} value={name}>
                      <ListItemText primary={tallyVersionsCount(selectedCategory, name, alreadySelectedObjects, objCatToData) as React.ReactNode}/>
                      </ArgosMenuItem>
                    ))}
                </Select>
              </FormControl>
            }

            {/*Version dropdown*/}
            {
              <FormControl sx={{width: WIDTH}} >
                <InputLabel id="version-select-label" shrink={!!selectedVersion}>Version</InputLabel>
                <Select
                  labelId="version-select-label"
                  id="version-select"
                  value={selectedVersion}
                  label="Version"
                  onChange={handleVersionChange}
                  variant="standard"
                >
                    {objCatToData[selectedCategory]?.[selectedName] && Object.keys(objCatToData[selectedCategory][selectedName]).map((version: string) => (
                    <ArgosMenuItem key={version} value={version} disabled={onlyVersion !== null && Number(version) !== onlyVersion}>
                      <ListItemText primary={version} />
                    </ArgosMenuItem>
                    ))}
                </Select>
              </FormControl>
            }

            {/*Variant dropdown*/}
            {
              <FormControl sx={{ width: WIDTH }}>
                <InputLabel id="variant-select-label">Variant</InputLabel>
                <Select
                  labelId="variant-select-label"
                  id="variant-select"
                  multiple
                  value={selectedVariant}
                  label="Variant"
                  onChange={handleVariantChange}
                  variant="standard"
                  renderValue={(selected) => selected.map((variant) => getProfileDisplayName(variant as TPmoAndMiscProfileNames)).join(", ")}
                >
                  <ArgosMenuItem
                    key="all"
                    value="all"
                    disabled={
                      selectedVersion !== null &&
                      objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants &&
                      Object.values(objCatToData[selectedCategory][selectedName][selectedVersion].variants).every((variantId: string) =>
                        alreadySelectedObjects.some((object: any) => object.objectId === variantId)
                      )
                    }
                  >
                    <ListItemIcon>
                      <ArgosCheckbox
                        checked={
                          selectedVersion !== null &&
                          selectedVariant.length > 0 &&
                          selectedVariant.length === Object.keys(objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants || {}).length
                        }
                        indeterminate={
                          selectedVariant.length > 0 &&
                          selectedVersion !== null &&
                          selectedVariant.length < Object.keys(objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants || {}).length
                        }
                        disabled={
                          selectedVersion !== null &&
                          objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants &&
                          Object.values(objCatToData[selectedCategory][selectedName][selectedVersion].variants).every((variantId: string) =>
                            alreadySelectedObjects.some((object: any) => object.objectId === variantId)
                          )
                        }
                      />
                    </ListItemIcon>
                    <ListItemText primary="Select All" />
                  </ArgosMenuItem>
                  {selectedVersion !== null && objCatToData[selectedCategory]?.[selectedName]?.[selectedVersion]?.variants &&
                    Object.entries(objCatToData[selectedCategory][selectedName][selectedVersion].variants).map(([variant, variantId]: [string, string]) => (
                      <ArgosMenuItem
                        key={variant}
                        value={variant}
                        disabled={alreadySelectedObjects.some((object: any) => object.objectId === variantId)}
                      >
                        <ListItemIcon>
                          <ArgosCheckbox
                            checked={selectedVariant.indexOf(variant) > -1}
                            disabled={alreadySelectedObjects.some((object: any) => object.objectId === variantId)}
                          />
                        </ListItemIcon>
                        <ListItemText primary={getProfileDisplayName(variant as TPmoAndMiscProfileNames)} />
                      </ArgosMenuItem>
                    ))}
                </Select>
              </FormControl>
            }
          </>
        )}

        {selectedDatasetPropertyType === PACKAGE_TYPE_STR && <FormControl sx={{width: WIDTH}}>
          <InputLabel id="category-select-label">
            Adas Program
          </InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            value={selectedAdasProgramName}
            label="Adas Program"
            onChange={e => setSelectedAdasProgramName(e.target.value)}
            variant="standard"
          >
            {Object.keys(adasProgramsToModelYears).map((option: string) => (
              <ArgosMenuItem key={option} value={option}>
                <ListItemText
                  primary={option}
                />
              </ArgosMenuItem>
            ))}
          </Select>
        </FormControl>}
        {selectedDatasetPropertyType == PACKAGE_TYPE_STR && <FormControl sx={{width: WIDTH}}>
          <InputLabel id="property-select-label">
            Model Year
          </InputLabel>
          <Select
            labelId="property-select-label"
            id="property-select"
            value={selectedModelYear}
            label="Model Year"
            onChange={e => setSelectedModelYear(e.target.value)}
            variant="standard"
          >
            {adasProgramsToModelYears[selectedAdasProgramName]?.map((option: string) => (
              <ArgosMenuItem key={option} value={option}>
                <ListItemText primary={option}/>
              </ArgosMenuItem>
            ))}
          </Select>
        </FormControl>}

        {selectedDatasetPropertyType === USE_CASE_TYPE_STR && <FormControl sx={{width: WIDTH}}>
          <ArgosTextField
            id="property-select"
            value={selectedOption}
            label="Property"
            onChange={handleUseCaseChange}
            variant="standard"/>
        </FormControl>}

        {multipleSelect && selectedDatasetPropertyType !== PACKAGE_TYPE_STR && selectedDatasetPropertyType !== USE_CASE_TYPE_STR && selectedDatasetPropertyType !== OBJECT_TYPE_STR && <FormControl sx={{width: WIDTH}}>
          <InputLabel id="property-mutiple-select-label">
            Property
          </InputLabel>
          <Select
            labelId="property-mutiple-select-label"
            id="property-multiple-select"
            multiple
            value={selectedOptions}
            label="Property"
            onChange={handleOptionsChange}
            variant="standard"
            renderValue={(selected) => selected.join(", ")}
          >
            <ArgosMenuItem key="all" value="all" disabled={selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? isAllInCurrentListAlreadySelected : false}>
              <ListItemIcon>
                <ArgosCheckbox
                  disabled={selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? isAllInCurrentListAlreadySelected : false}
                  checked={isAllSelected}
                  indeterminate={
                    selectedOptions.length > 0 && selectedOptions.length < optionsList.length
                  }
                />
              </ListItemIcon>
              <ListItemText
                primary="Select All"
              />
            </ArgosMenuItem>
            {optionsList?.map((option: any) => (
              <ArgosMenuItem
                disabled={selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? isOptionAlreadySelected(option, selectedDatasetPropertyType, props.configState, alreadySelectedObjects) : false}
                key={option} value={option}
              >
                <ListItemIcon>
                  <ArgosCheckbox
                    disabled={selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? isOptionAlreadySelected(option, selectedDatasetPropertyType, props.configState, alreadySelectedObjects) : false}
                    checked={selectedOptions.indexOf(option) > -1 ||
                      (selectedDatasetPropertyType === ROAD_TYPE_TYPE_STR ? isOptionAlreadySelected(option, selectedDatasetPropertyType, props.configState, alreadySelectedObjects) : false)}
                  />
                </ListItemIcon>
                <ListItemText primary={option}/>
              </ArgosMenuItem>
            ))}
          </Select>
        </FormControl>}

        {!multipleSelect && selectedDatasetPropertyType !== PACKAGE_TYPE_STR && selectedDatasetPropertyType !== USE_CASE_TYPE_STR && selectedDatasetPropertyType !== OBJECT_TYPE_STR && <FormControl sx={{width: WIDTH}}>
          <InputLabel id="property-select-label">
            Property
          </InputLabel>
          <Select
            labelId="property-select-label"
            id="property-select"
            value={selectedOption}
            label="Property"
            onChange={handleOptionsChange}
            variant="standard"
            disabled={selectedDatasetPropertyType === OBJECT_TYPE_STR && selectedCategory === ""}
          >
            {optionsList?.map((option: string) => (
              <ArgosMenuItem key={option} value={option}>
                <ListItemText primary={option}/>
              </ArgosMenuItem>
            ))}
          </Select>
        </FormControl>}

        {/*Bottom buttons (Cancel, Add)*/}
        <ArgosFlexbox>
          <ArgosButton onClick={() => props.onClose()}>
            Cancel
          </ArgosButton>
          <ArgosButton onClick={() => saveData()} disabled={isSaveDisabled}>
            Add
          </ArgosButton>
        </ArgosFlexbox>
      </ArgosFlexbox>
    </Loader>
  )
}

export default AddPropertyDialog

/**
 * Checks if a given option in driving scenario properties list is already selected.
 *
 * @param option - The option to check, containing an `id` property.
 * @param selectedType - The type of selection to check within the config state, ex: "Road Type"
 * @param configState - Optional current saved properties state (for bulk editing).
 * @param alreadySelectedObjects - Optional array of already selected objects (for single datasets).
 * @returns `true` if the option is already selected, `false` otherwise.
 */
export const isOptionAlreadySelected = (
  option: { id: string },
  selectedType: string,
  configState?: { [key: string]: any },
  alreadySelectedObjects?: any
) => {
  if (selectedType !== ROAD_TYPE_TYPE_STR) {
    return false
  }

  if (!alreadySelectedObjects && configState && configState[selectedType]) {
    return configState[selectedType]?.some((object: any) => object.objectId === option || object.objectId === option.id)
  }
  if (alreadySelectedObjects) {
    return alreadySelectedObjects.some((object: any) => object.name === option)
  }
  return false
}
